/**
 * WebSocket + HTTP client for the E2EE relay (web port).
 *
 * Mirrors `mobile/src/sync/relay.ts` byte-for-byte at the protocol
 * level: same `identify` shape on connect, same `sync` /
 * `sync_request` / `sync_response` / `sync_full` / `wipe` /
 * `list_workers` / `set_role` / `push_token` / `push_meta` message
 * types, same exponential backoff (1s → 2s → 4s → 8s → 16s → 30s
 * cap), same 30-second HTTP-poll safety net, same offline queue that
 * drains on reconnect.
 *
 * What's deliberately different from mobile:
 *
 *   - No `expo-*` dependencies. WebSocket and fetch come from the
 *     browser globals (or `globalThis.WebSocket` in tests).
 *   - No "background" lifecycle hook. Mobile reconnects on
 *     `AppState.active`; the web reconnects on `online` + `visibility`
 *     events instead. That's wired in `App.tsx` later — this module
 *     exposes `reconnect()` + `pollImmediate()` so the lifecycle
 *     glue stays at the integration layer.
 *   - The relay is REIMPLEMENTED, not copy-pasted. Mobile uses
 *     `class RelayClient` with a module-level singleton; we keep the
 *     same shape (singleton `relay` export) but re-author every
 *     method against browser APIs so future divergence (e.g. web
 *     pre-flight token, terminal id) is local to this file.
 *   - Handler arrays are small (~5 items max) and mutations come
 *     from React effect cleanups; we expose `off*` unsubscribers so
 *     a route that mounts/unmounts repeatedly can't leak listeners.
 *     Mobile leaks them today (well-known caveat); we fix it here
 *     because the web mounts and unmounts routes far more often.
 *
 * The `RelayClient` class is exported alongside the singleton so
 * tests can spin up isolated instances and pass a mock WebSocket
 * factory. Production code keeps using the singleton.
 */

import { RELAY_HTTP_URL, RELAY_URL } from '../config/env';

// ── Tunables ────────────────────────────────────────────────────────

export const BACKOFF_INITIAL_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;
export const POLL_INTERVAL_MS = 30_000;

// ── Types ───────────────────────────────────────────────────────────

export type Role = 'creator' | 'admin' | 'member';

export interface IdentifyParams {
  companyId: string;
  workerId: string;
  name: string;
  isAdmin: boolean;
  companyToken: string;
  role?: Role;
}

export interface WorkerListEntry {
  id: string;
  name: string;
  is_admin?: boolean;
  role?: Role;
  registered_at?: number;
}

export type SyncHandler = (encryptedData: string, serverTs?: number) => void;
export type SyncRequestHandler = (requesterId: string) => void;
export type SyncFullHandler = (encryptedData: string) => void;
export type WipeHandler = () => void;
export type WorkersHandler = (list: WorkerListEntry[]) => void;
export type GenericMessageHandler = (message: RelayMessage) => void;
/** Spec 217 PR4.b — fires when the creator renames the team. The
 *  argument is the new (trimmed) name. */
export type CompanyRenamedHandler = (name: string) => void;
/** Spec 200 Fase 5 #7 — fired when the relay rejects an
 *  authenticated request with 401 (terminal revoked, token
 *  rotated). Subscribers are expected to wipe local state and
 *  redirect the user to /pair. */
export type AuthErrorHandler = () => void;

/** Loose schema for messages the relay sends. We keep `Record` open
 *  because the wire is a moving target and the routing logic only
 *  cares about `type`. */
export interface RelayMessage {
  type: string;
  [key: string]: unknown;
}

/** Removable subscription. Returned by every `on*` so React effect
 *  cleanups can stay terse: `useEffect(() => relay.onSync(h), [])`. */
export type Unsubscribe = () => void;

/**
 * Test-friendly WebSocket factory. Production passes `undefined` (or
 * omits) → the global `WebSocket` is used. Tests pass a constructor
 * that returns a stub.
 */
export type WebSocketFactory = (url: string) => WebSocket;

// ── RelayClient ─────────────────────────────────────────────────────

export class RelayClient {
  private ws: WebSocket | null = null;
  private wsUrl: string;
  private httpUrl: string;
  private wsFactory: WebSocketFactory;

  // identify state — survives reconnects so the next `_connect()`
  // can re-identify with the same credentials.
  private identifyParams: IdentifyParams | null = null;

  // handlers — arrays of subscribers per message type.
  private syncHandlers: SyncHandler[] = [];
  private syncRequestHandlers: SyncRequestHandler[] = [];
  private syncFullHandlers: SyncFullHandler[] = [];
  private wipeHandlers: WipeHandler[] = [];
  private workersHandlers: WorkersHandler[] = [];
  private genericHandlers: GenericMessageHandler[] = [];
  private authErrorHandlers: AuthErrorHandler[] = [];
  private companyRenamedHandlers: CompanyRenamedHandler[] = [];

  // queue + connection state
  private queue: string[] = [];
  private connected = false;
  private backoffDelay = BACKOFF_INITIAL_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  // Disconnect flag prevents `onclose` from re-arming a reconnect
  // when the caller asked to tear the connection down on purpose.
  private intentionallyClosed = false;

  constructor(opts?: { wsUrl?: string; httpUrl?: string; wsFactory?: WebSocketFactory }) {
    this.wsUrl = opts?.wsUrl ?? RELAY_URL;
    this.httpUrl = opts?.httpUrl ?? RELAY_HTTP_URL;
    this.wsFactory = opts?.wsFactory ?? ((url) => new WebSocket(url));
  }

  // ── Connection lifecycle ──────────────────────────────────────────

  /** Open the WebSocket and start the HTTP poll safety net. Replaces
   *  any previous connection (for cases like company switch, role
   *  change). */
  connect(params: IdentifyParams): void {
    this.identifyParams = params;
    this.intentionallyClosed = false;
    this.backoffDelay = BACKOFF_INITIAL_MS;

    // Tear down any previous socket BEFORE _connect so we don't leak
    // a listener that re-arms the queue from an old session.
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // .close() can throw on a half-open socket; the close-state
        // is what we want anyway, so the throw is harmless.
      }
      this.ws = null;
    }

    this._connect();
    this._startPolling();
  }

  /** Force an immediate reconnection (e.g. from a `visibilitychange`
   *  handler). No-op if already connected. */
  reconnect(): void {
    if (this.connected) return;
    if (!this.identifyParams) return;
    this.backoffDelay = BACKOFF_INITIAL_MS;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // see comment above
      }
      this.ws = null;
    }
    this._connect();
  }

  /** Tear everything down. Subsequent `connect()` calls re-init. */
  disconnect(): void {
    this.intentionallyClosed = true;
    this._stopPolling();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // see comment above
      }
      this.ws = null;
    }
    this.connected = false;
  }

  get isConnected(): boolean {
    return this.connected;
  }

  get token(): string | null {
    return this.identifyParams?.companyToken ?? null;
  }

  // ── Internal connect path ─────────────────────────────────────────

  private _connect(): void {
    if (this.intentionallyClosed) return;
    if (this.ws && this.connected) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    let ws: WebSocket;
    try {
      ws = this.wsFactory(this.wsUrl);
    } catch {
      // Synchronous WebSocket constructor failure (rare; bad URL,
      // CSP block). Schedule a retry so we don't lose connection
      // recovery just because the host policy bounced us once.
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      // Guard against a `connect()` race: a newer call may have
      // replaced this.ws between scheduling this onopen and its
      // firing. Sending on the stale socket throws, so bail.
      if (this.ws !== ws) return;
      if (ws.readyState !== WebSocket.OPEN) return;
      this.connected = true;
      this.backoffDelay = BACKOFF_INITIAL_MS;
      // First message MUST be `identify` — relay rejects everything
      // else until it has a companyId/workerId/token.
      if (this.identifyParams) {
        const { companyId, workerId, name, isAdmin, companyToken, role } = this.identifyParams;
        this._rawSend(
          ws,
          JSON.stringify({
            type: 'identify',
            companyId,
            workerId,
            name,
            isAdmin,
            role: role ?? (isAdmin ? 'admin' : 'member'),
            companyToken,
          }),
        );
      }
      // Drain any messages that piled up while the socket was closed.
      while (this.queue.length > 0 && ws.readyState === WebSocket.OPEN) {
        const next = this.queue.shift();
        if (next === undefined) break;
        if (!this._rawSend(ws, next)) {
          // Re-queue at the head and bail; we'll drain again on the
          // next reconnect.
          this.queue.unshift(next);
          break;
        }
      }
      // Refresh the worker list on (re)connect so any Settings UI
      // that depends on it doesn't render a stale roster.
      this.requestWorkers();
      // Spec 200 Fase 8 — drain `pending_messages` from the relay
      // immediately after a (re)connect. The relay already fans
      // pending out at identify time on its side, but a network
      // hiccup that closed the WS *while a peer was broadcasting*
      // leaves a window where the queue has rows the relay
      // delivered to a stale socket. The HTTP polling fallback
      // hits `/pending/:workerId` and re-drains anything that's
      // left, idempotently — `_processMessage()` already dedups by
      // serverTs / id at the store layer. Belt-and-suspenders so
      // mobile→web sync feels real-time without a `page.reload()`
      // workaround. Wrapped in setTimeout(0) so the synchronous
      // identify above wins the first-message slot.
      setTimeout(() => {
        void this._poll();
      }, 0);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data as string) as RelayMessage;
        this._processMessage(message);
      } catch {
        // Non-JSON payload — relay never sends those today, but we
        // refuse to throw here because the WebSocket spec calls for
        // best-effort message handling.
      }
    };

    ws.onclose = (event) => {
      this.connected = false;
      if (this.intentionallyClosed) return;
      // Spec 200 Fase 8 — relay closes revoked web terminals with
      // custom code 4401 (`session revoked`). Treat it as the same
      // session-end signal that an HTTP 401 from /pending fires:
      // stop polling, notify auth-error subscribers (App.tsx clears
      // IndexedDB + bounces to /pair). Skip the reconnect schedule
      // — the user has been logged out, retrying would just hit
      // the same 4401 again.
      if (event.code === 4401) {
        this.intentionallyClosed = true;
        this._stopPolling();
        for (const h of this.authErrorHandlers) {
          try {
            h();
          } catch {
            /* one bad handler shouldn't block the rest */
          }
        }
        return;
      }
      this._scheduleReconnect();
    };

    ws.onerror = () => {
      // Force a close so onclose runs the reconnect path. If the
      // browser already closed it, this is a no-op.
      try {
        ws.close();
      } catch {
        // see comment above
      }
    };
  }

  private _scheduleReconnect(): void {
    if (this.reconnectTimer) return; // one already armed
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._connect();
    }, this.backoffDelay);
    // Exponential backoff: 1 → 2 → 4 → 8 → 16 → 30 (cap). Mirrors
    // `mobile/src/sync/relay.ts`.
    this.backoffDelay = Math.min(this.backoffDelay * 2, BACKOFF_MAX_MS);
  }

  // ── HTTP polling safety net ───────────────────────────────────────

  private _startPolling(): void {
    this._stopPolling();
    this.pollTimer = setInterval(() => {
      void this._poll();
    }, POLL_INTERVAL_MS);
  }

  private _stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async _poll(): Promise<void> {
    if (!this.identifyParams) return;
    await this._httpPoll();
    if (this.connected) {
      this.requestSync();
    }
  }

  private async _httpPoll(): Promise<void> {
    if (!this.identifyParams) return;
    // After a 401 we mark the client `intentionallyClosed` so the
    // background interval stops; manual `pollImmediate()` would
    // otherwise re-fire the auth-error handler on every call. Guard
    // here so a single 401 produces exactly one notification.
    if (this.intentionallyClosed) return;
    const { workerId, companyToken } = this.identifyParams;
    try {
      const res = await fetch(`${this.httpUrl}/pending/${workerId}`, {
        headers: companyToken ? { 'x-company-token': companyToken } : {},
      });
      // Spec 200 Fase 5 #7 — terminal revoked surfaces as 401 here.
      // Tear down + notify subscribers so the App layer can wipe
      // IndexedDB and bounce the user to /pair. Stop polling so
      // the wipe-and-redirect can run once cleanly.
      if (res.status === 401) {
        this.intentionallyClosed = true;
        this._stopPolling();
        for (const h of this.authErrorHandlers) {
          try {
            h();
          } catch {
            /* ignore — one bad handler shouldn't block the rest */
          }
        }
        return;
      }
      if (!res.ok) return;
      const messages = (await res.json()) as RelayMessage[];
      if (Array.isArray(messages) && messages.length > 0) {
        for (const message of messages) {
          this._processMessage(message);
        }
      }
    } catch {
      // Network blip — silent. The next poll runs in 30s.
    }
  }

  /** Force an immediate HTTP poll (call from `online`/`visible`
   *  hooks; complements `reconnect()`). */
  async pollImmediate(): Promise<void> {
    await this._httpPoll();
  }

  // ── Message routing ───────────────────────────────────────────────

  private _processMessage(message: RelayMessage): void {
    if (message.type === 'wipe') {
      this.wipeHandlers.forEach((h) => h());
      return;
    }
    if (message.type === 'workers' && Array.isArray(message.list)) {
      const list = message.list as WorkerListEntry[];
      this.workersHandlers.forEach((h) => h(list));
      return;
    }
    if (message.type === 'sync' && typeof message.data === 'string') {
      const ts = typeof message.serverTs === 'number' ? message.serverTs : undefined;
      this.syncHandlers.forEach((h) => h(message.data as string, ts));
      return;
    }
    if (message.type === 'sync_request' && typeof message.requesterId === 'string') {
      this.syncRequestHandlers.forEach((h) => h(message.requesterId as string));
      return;
    }
    if (message.type === 'sync_full' && typeof message.data === 'string') {
      this.syncFullHandlers.forEach((h) => h(message.data as string));
      return;
    }
    if (message.type === 'company_renamed' && typeof message.name === 'string') {
      this.companyRenamedHandlers.forEach((h) => h(message.name as string));
      return;
    }
    // Unrecognised types fall through to the generic bucket. UI code
    // can opt in via `onMessage` for things like push_meta echoes.
    this.genericHandlers.forEach((h) => h(message));
  }

  // ── Sending ───────────────────────────────────────────────────────

  /** Low-level send with readyState guard. Returns true if sent. */
  private _rawSend(ws: WebSocket, payload: string): boolean {
    if (ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(payload);
      return true;
    } catch {
      return false;
    }
  }

  /** Queue if not connected; useful for messages that MUST eventually
   *  reach the relay (sync upserts, wipes, set_role). */
  private _sendOrQueue(payload: string): void {
    if (this.ws && this._rawSend(this.ws, payload)) return;
    this.queue.push(payload);
  }

  /** Best-effort send; drops if not connected. Useful for read-only
   *  requests like `list_workers`, `sync_request` — the next poll
   *  will trigger them anyway. */
  private _sendBestEffort(payload: string): void {
    if (this.ws) this._rawSend(this.ws, payload);
  }

  // ── Public send helpers (parity with mobile) ──────────────────────

  send(encryptedBlob: string): void {
    if (!this.identifyParams) return;
    this._sendOrQueue(
      JSON.stringify({
        type: 'sync',
        companyId: this.identifyParams.companyId,
        data: encryptedBlob,
      }),
    );
  }

  sendWipe(targetId: string): void {
    if (!this.identifyParams) return;
    this._sendOrQueue(
      JSON.stringify({
        type: 'wipe',
        companyId: this.identifyParams.companyId,
        targetId,
      }),
    );
  }

  requestWorkers(): void {
    this._sendBestEffort(JSON.stringify({ type: 'list_workers' }));
  }

  setMemberRole(targetId: string, role: 'admin' | 'member'): void {
    this._sendOrQueue(JSON.stringify({ type: 'set_role', targetId, role }));
  }

  /** Spec 217 PR4.b — rename the team. Creator-only on the relay
   *  side (FORBIDDEN otherwise). The new name is trimmed + length-
   *  capped at 80 chars; the relay rejects out-of-range values
   *  with `BAD_NAME`. On success the relay broadcasts a
   *  `company_renamed` message which `onCompanyRenamed` fires for. */
  renameCompany(name: string): void {
    this._sendOrQueue(JSON.stringify({ type: 'rename_company', name }));
  }

  requestSync(): void {
    this._sendBestEffort(JSON.stringify({ type: 'sync_request' }));
  }

  respondSync(targetId: string, encryptedData: string): void {
    this._sendBestEffort(JSON.stringify({ type: 'sync_response', targetId, data: encryptedData }));
  }

  sendPushToken(pushToken: string): void {
    this._sendOrQueue(JSON.stringify({ type: 'push_token', token: pushToken }));
  }

  sendPushMeta(workId: string, title: string, assignedWorkers: string[]): void {
    this._sendOrQueue(JSON.stringify({ type: 'push_meta', workId, title, assignedWorkers }));
  }

  // ── Subscriptions ─────────────────────────────────────────────────

  onSync(handler: SyncHandler): Unsubscribe {
    this.syncHandlers.push(handler);
    return () => {
      const idx = this.syncHandlers.indexOf(handler);
      if (idx >= 0) this.syncHandlers.splice(idx, 1);
    };
  }

  onSyncRequest(handler: SyncRequestHandler): Unsubscribe {
    this.syncRequestHandlers.push(handler);
    return () => {
      const idx = this.syncRequestHandlers.indexOf(handler);
      if (idx >= 0) this.syncRequestHandlers.splice(idx, 1);
    };
  }

  onSyncFull(handler: SyncFullHandler): Unsubscribe {
    this.syncFullHandlers.push(handler);
    return () => {
      const idx = this.syncFullHandlers.indexOf(handler);
      if (idx >= 0) this.syncFullHandlers.splice(idx, 1);
    };
  }

  onWipe(handler: WipeHandler): Unsubscribe {
    this.wipeHandlers.push(handler);
    return () => {
      const idx = this.wipeHandlers.indexOf(handler);
      if (idx >= 0) this.wipeHandlers.splice(idx, 1);
    };
  }

  onWorkers(handler: WorkersHandler): Unsubscribe {
    this.workersHandlers.push(handler);
    return () => {
      const idx = this.workersHandlers.indexOf(handler);
      if (idx >= 0) this.workersHandlers.splice(idx, 1);
    };
  }

  onMessage(handler: GenericMessageHandler): Unsubscribe {
    this.genericHandlers.push(handler);
    return () => {
      const idx = this.genericHandlers.indexOf(handler);
      if (idx >= 0) this.genericHandlers.splice(idx, 1);
    };
  }

  /** Spec 217 PR4.b — subscribe to team rename broadcasts. The handler
   *  receives the new trimmed name; the caller is expected to refresh
   *  any local cache (e.g. the IndexedDB identity row). */
  onCompanyRenamed(handler: CompanyRenamedHandler): Unsubscribe {
    this.companyRenamedHandlers.push(handler);
    return () => {
      const idx = this.companyRenamedHandlers.indexOf(handler);
      if (idx >= 0) this.companyRenamedHandlers.splice(idx, 1);
    };
  }

  /** Spec 200 Fase 5 #7 — auth error notification. Fires when the
   *  relay rejects an authenticated request with 401. Subscribers
   *  should wipe local state + redirect to /pair. The relay also
   *  stops polling on the same event so the app gets a single
   *  clean teardown rather than a stream of 401s. */
  onAuthError(handler: AuthErrorHandler): Unsubscribe {
    this.authErrorHandlers.push(handler);
    return () => {
      const idx = this.authErrorHandlers.indexOf(handler);
      if (idx >= 0) this.authErrorHandlers.splice(idx, 1);
    };
  }

  // ── Test introspection ────────────────────────────────────────────
  //
  // These getters are package-private — they exist so unit tests can
  // assert internal state without exposing it via TypeScript's public
  // surface (production code MUST NOT touch these).

  /** @internal */
  get _queueDepth(): number {
    return this.queue.length;
  }

  /** @internal */
  get _backoffMs(): number {
    return this.backoffDelay;
  }
}

/** Singleton — production code consumes the relay through this. */
export const relay = new RelayClient();

// Spec 200 §10 Nivel 1 — expose the singleton on a globalThis hook
// so the Playwright suite can drive `pollImmediate()` from inside
// the page without re-bundling. Production builds carry this too
// (no surface-area downside; the property name is namespaced and
// the API is identical to what import { relay } gives anyone with
// access to the bundle anyway).
if (typeof globalThis !== 'undefined') {
  (globalThis as unknown as { __cuadrillaRelay?: RelayClient }).__cuadrillaRelay = relay;
}
